import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, resolveMediaUrl } from "@/lib/api";
import { toast } from "sonner";
import { Users, Clock, CheckCircle, RefreshCw, RotateCw, Wand2, CheckCheck, X } from "lucide-react";

// ─── Image Review Modal ───────────────────────────────────────────────────────
function ImageReviewModal({ session, onConfirm, onCancel }) {
  const [rotation, setRotation] = useState(0);
  const [enhancing, setEnhancing] = useState(false);
  const [enhanced, setEnhanced] = useState(false);
  const [currentUrl, setCurrentUrl] = useState(session.room_image_url);
  const [applying, setApplying] = useState(false);
  const [imgSize, setImgSize] = useState({ w: 0, h: 0 });
  const imgRef = useRef(null);

  const isRotated = rotation % 180 !== 0;

  // instant CSS rotation — baked on confirm
  const rotate = () => {
    if (enhancing) return;
    setRotation((r) => (r + 90) % 360);
  };

  const bakeAndConfirm = async () => {
    if (enhancing || applying) return;
    if (rotation === 0) { onConfirm(currentUrl); return; }
    setApplying(true);
    try {
      const resp = await fetch(resolveMediaUrl(currentUrl));
      const blob = await resp.blob();
      const bitmap = await createImageBitmap(blob);
      const deg = rotation;
      const sw = deg === 90 || deg === 270 ? bitmap.height : bitmap.width;
      const sh = deg === 90 || deg === 270 ? bitmap.width : bitmap.height;
      const canvas = document.createElement("canvas");
      canvas.width = sw;
      canvas.height = sh;
      const ctx = canvas.getContext("2d");
      ctx.translate(sw / 2, sh / 2);
      ctx.rotate((deg * Math.PI) / 180);
      ctx.drawImage(bitmap, -bitmap.width / 2, -bitmap.height / 2);
      const outBlob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", 0.92));
      const file = new File([outBlob], "rotated_room.jpg", { type: "image/jpeg" });
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onConfirm(data.url);
    } catch {
      toast.error("Failed to apply rotation");
      setApplying(false);
    }
  };

  const enhance = async () => {
    setEnhancing(true);
    try {
      const { data } = await api.post("/sales/enhance-room", { room_image_url: currentUrl });
      setCurrentUrl(data.enhanced_url);
      setEnhanced(true);
      toast.success("Image enhanced!");
    } catch {
      toast.error("Enhancement failed");
    } finally {
      setEnhancing(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
      <div className="bg-[#1A1C1E] rounded-2xl w-full max-w-lg shadow-2xl flex flex-col overflow-y-auto" style={{ maxHeight: "95vh" }}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10 shrink-0">
          <div>
            <h3 className="text-white font-semibold text-lg">Review Room Photo</h3>
            <p className="text-xs text-white/50 mt-0.5">
              {session.customer_name || "Anonymous"}{session.customer_phone ? ` · ${session.customer_phone}` : ""}
            </p>
          </div>
          <button onClick={onCancel} className="text-white/50 hover:text-white p-1">
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Image area */}
        <div className="bg-black flex items-center justify-center relative"
          style={isRotated && imgSize.w && imgSize.h ? { height: `${imgSize.w * (imgSize.w / imgSize.h)}px` } : {}}
        >
          <img
            ref={imgRef}
            src={resolveMediaUrl(currentUrl)}
            alt="room"
            crossOrigin="anonymous"
            onLoad={(e) => setImgSize({ w: e.target.naturalWidth, h: e.target.naturalHeight })}
            style={{
              display: "block",
              transition: "transform 0.2s",
              transform: `rotate(${rotation}deg)`,
              transformOrigin: "center center",
              width: isRotated && imgSize.w && imgSize.h ? `${imgSize.h / imgSize.w * 100}%` : "100%",
              height: "auto",
            }}
          />
          {(enhancing || applying) && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/60" style={{ zIndex: 10 }}>
              <div className="text-center text-white">
                <div className="h-1.5 w-32 rounded-full overflow-hidden bg-white/10 mx-auto">
                  <div className="h-full w-full shimmer-progress" />
                </div>
                <p className="mt-3 text-sm">{enhancing ? "Enhancing…" : "Uploading…"}</p>
              </div>
            </div>
          )}
          {enhanced && (
            <span className="absolute top-3 right-3 bg-green-600 text-white text-[10px] font-medium px-2 py-0.5 rounded-full" style={{ zIndex: 10 }}>
              Enhanced ✓
            </span>
          )}
        </div>

        {/* Actions — always visible at bottom */}
        <div className="px-6 py-4 flex flex-col gap-3 shrink-0 border-t border-white/10">
          <div className="flex gap-3">
            <button
              onClick={rotate}
              disabled={enhancing || applying}
              className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border border-white/20 text-white/80 hover:bg-white/10 disabled:opacity-40 transition text-sm"
            >
              <RotateCw className="h-4 w-4" /> Rotate 90°
            </button>
            <button
              onClick={enhance}
              disabled={enhancing || applying || enhanced}
              className={`flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border text-sm font-medium transition disabled:opacity-40 ${
                enhanced ? "border-green-500 text-green-400 bg-green-500/10" : "border-white/20 text-white/80 hover:bg-white/10"
              }`}
            >
              <Wand2 className="h-4 w-4" />
              {enhanced ? "Enhanced ✓" : enhancing ? "Enhancing…" : "Enhance Photo"}
            </button>
          </div>
          <button
            onClick={bakeAndConfirm}
            disabled={enhancing || applying}
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-white text-[#1A1C1E] font-semibold text-sm disabled:opacity-40 transition hover:bg-white/90"
          >
            <CheckCheck className="h-4 w-4" />
            {applying ? "Uploading…" : "Looks good — Open Visualizer"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── SalesQueue ───────────────────────────────────────────────────────────────
export default function SalesQueue() {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [claiming, setClaiming] = useState(null);
  const [reviewSession, setReviewSession] = useState(null); // session being reviewed

  const load = () => {
    api.get("/sales/queue").then((r) => setSessions(r.data)).catch(() => {});
  };

  useEffect(() => {
    load();
    const interval = setInterval(load, 10000);
    return () => clearInterval(interval);
  }, []);

  const openReview = (session, claimedUrl) => {
    // claimedUrl may differ from session.room_image_url if already modified
    setReviewSession({ ...session, room_image_url: claimedUrl || session.room_image_url });
  };

  const claim = async (session) => {
    setClaiming(session.id);
    try {
      await api.post(`/sales/queue/${session.id}/claim`);
      openReview(session);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not claim session");
      load();
    } finally {
      setClaiming(null);
    }
  };

  const reclaim = async (session) => {
    setClaiming(session.id);
    try {
      await api.post(`/sales/queue/${session.id}/reclaim`);
      openReview(session);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not re-claim session");
      load();
    } finally {
      setClaiming(null);
    }
  };

  const handleReviewConfirm = (finalUrl) => {
    const s = reviewSession;
    setReviewSession(null);
    navigate(`/sales/visualizer?session=${s.id}&room=${encodeURIComponent(finalUrl)}`);
  };

  if (sessions.length === 0) {
    return (
      <div className="neivai-card p-10 text-center text-[#8A8D91] flex flex-col items-center gap-3">
        <Users className="h-8 w-8 text-[#C86B53]/40" />
        <p>No customers waiting right now.</p>
      </div>
    );
  }

  return (
    <>
      {reviewSession && (
        <ImageReviewModal
          session={reviewSession}
          onConfirm={handleReviewConfirm}
          onCancel={() => { setReviewSession(null); load(); }}
        />
      )}

      <div className="space-y-3">
        {sessions.map((s) => (
          <div key={s.id} className="neivai-card p-4 flex items-center gap-4">
            <img
              src={resolveMediaUrl(s.room_image_url)}
              alt="room"
              className="h-16 w-16 rounded-lg object-cover shrink-0 bg-[#F3F1EC]"
            />
            <div className="flex-1 min-w-0">
              <p className="font-medium text-sm truncate">
                {s.customer_name || "Anonymous customer"}
              </p>
              {s.customer_phone && (
                <p className="text-xs text-[#5C5F62]">{s.customer_phone}</p>
              )}
              <p className="text-xs text-[#8A8D91] flex items-center gap-1 mt-0.5">
                <Clock className="h-3 w-3" />
                {new Date(s.created_at).toLocaleTimeString()}
                {s.status === "claimed" && s.claimed_by_name && (
                  <span className="ml-2 text-amber-600">· claimed by {s.claimed_by_name}</span>
                )}
              </p>
              {s.reclaim_count > 0 && (
                <span className="inline-flex items-center gap-1 mt-1 text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-orange-50 text-orange-600 border border-orange-200">
                  <RefreshCw className="h-2.5 w-2.5" /> Re-claimed {s.reclaim_count}×
                </span>
              )}
            </div>
            {s.status === "waiting" ? (
              <button
                onClick={() => claim(s)}
                disabled={claiming === s.id}
                className="neivai-btn-primary text-sm shrink-0 disabled:opacity-60"
              >
                {claiming === s.id ? "Claiming…" : "Pick up"}
              </button>
            ) : (
              <div className="flex flex-col items-end gap-1.5 shrink-0">
                <span className="text-xs text-amber-600 flex items-center gap-1">
                  <CheckCircle className="h-3.5 w-3.5" /> Claimed
                </span>
                <button
                  onClick={() => reclaim(s)}
                  disabled={claiming === s.id}
                  className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full border border-[#C86B53] text-[#C86B53] hover:bg-[#C86B53]/10 disabled:opacity-60 transition"
                >
                  <RefreshCw className="h-3 w-3" />
                  {claiming === s.id ? "Re-claiming…" : "Re-claim"}
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    </>
  );
}
